import React, { useContext, useMemo } from "react"
import { ErrorBoundary } from "@sentry/react"
import { ERROR } from "helpers/data-statuses"
import type { OPLayoutComponent } from "connected-components/op-layout-engine/types"
import { getModuleComponentName, generatePanelId } from "connected-components/op-layout-engine/panel-id-utils"
import { getModuleDefinition, isModuleTypeRegistered } from "../modules/registry"
import { MODULE_TYPES, ModuleRenderDependencies, DashboardModule, ModuleType } from "../modules/types"
import type { ModuleConfig, ModuleDefinition } from "../modules/types"
import { PanelIdProvider, PanelTitleProvider } from "../context/panel-id"
import { downloadRegistry } from "../context/download-registry"
import { warn } from "helpers/dev-logger"
import {
  DashboardOpLayoutModuleDependenciesContext,
  mergeModuleRenderDependenciesWithDashboardOpLayout,
} from "../context/dashboard-op-layout-module-dependencies"

type ModuleRenderer = (dependencies: ModuleRenderDependencies, moduleId: string) => React.ReactElement

export const SECONDARY_MODULE_CONFIG_VIEW_KEYS = {
  INITIAL_VIEW: "initialView",
  INITIAL_TAB: "initialTab",
} as const

const SECONDARY_MODULE_TYPES: ModuleType[] = [
  MODULE_TYPES.ASSET_VIEW,
  MODULE_TYPES.KPI,
  MODULE_TYPES.GRAPH,
  MODULE_TYPES.FACTORS,
]

export function getPanelIdForModule(module: DashboardModule): string {
  return generatePanelId(getModuleComponentName(module.type, module.id), 0)
}

export function isModuleRemovable(module: DashboardModule, definition: ModuleDefinition): boolean {
  const isSecondary = module.config?.isSecondary === true
  return (SECONDARY_MODULE_TYPES.includes(module.type) && isSecondary) || definition.removable
}

export const DUPLICATE_TITLE_SUFFIX = " (copy)"

/**
 * Shared config for opening a secondary dashboard module from a searchable menu row
 * (Shift+click passes floating via the pointer event).
 */
export function buildSecondaryModuleConfigFromMenuItem(
  item: { label: string, id: string },
  event: { shiftKey?: boolean } | undefined,
  viewKey: (typeof SECONDARY_MODULE_CONFIG_VIEW_KEYS)[keyof typeof SECONDARY_MODULE_CONFIG_VIEW_KEYS],
): ModuleConfig {
  return {
    title: item.label,
    [viewKey]: item.id,
    isSecondary: true,
    ...(event?.shiftKey === true && { floating: true }),
  }
}

export function buildAssetViewConfigForNewTab(payload: { title: string, initialView: string, floating?: boolean }): ModuleConfig {
  const event = payload.floating === true ? { shiftKey: true as const } : undefined
  return buildSecondaryModuleConfigFromMenuItem(
    { label: payload.title, id: payload.initialView },
    event,
    SECONDARY_MODULE_CONFIG_VIEW_KEYS.INITIAL_VIEW,
  )
}

export function buildModuleConfigForDuplicate(module: DashboardModule, baseTitle: string): ModuleConfig {
  return {
    ...module.config,
    isSecondary: true,
    // Record the immediate source panel (overriding any inherited value if the source was itself
    // a duplicate). Lets an asset-view clone hydrate from the source's persisted prefs when the
    // source was never mounted and clonePanelState had nothing to copy. Other module types ignore it.
    duplicateFromPanelId: getPanelIdForModule(module),
    title: `${baseTitle}${DUPLICATE_TITLE_SUFFIX}`,
  }
}

/** Sole primary asset-view uses legacy prefs unless `config.shouldUseLegacyAssetViewPreferences` overrides. */
export function getShouldUseLegacyAssetViewPreferences(
  module: DashboardModule,
  allModules: DashboardModule[],
): boolean {
  if (module.type !== MODULE_TYPES.ASSET_VIEW) {
    return false
  }
  const override = module.config?.shouldUseLegacyAssetViewPreferences
  if (typeof override === "boolean") {
    return override
  }
  const assetViewModules = allModules.filter((m) => m.type === MODULE_TYPES.ASSET_VIEW)
  if (assetViewModules.length !== 1) {
    return false
  }
  if (module.config?.isSecondary === true) {
    return false
  }
  return assetViewModules[0].id === module.id
}

export function createAssetViewSyncDashboardModuleConfig(
  updateModule: (moduleId: string, updates: Partial<DashboardModule>) => void,
  moduleId: string,
) {
  return (configUpdates: Partial<ModuleConfig>) => {
    updateModule(moduleId, { config: configUpdates })
  }
}

interface CreateComponentDependencies extends ModuleRenderDependencies {
  onRemoveModule?: (moduleId: string, requiresConfirmation?: boolean) => void
  onUpdateModuleTitle?: (moduleId: string, newTitle: string) => void
  onDuplicateModule?: (moduleId: string) => void
  updateModule?: (moduleId: string, updates: Partial<DashboardModule>) => void
}

/**
 * OpLayoutEngine embeds Dockview; Dockview keeps the first React panel content factory per panel, so
 * {@link ModuleRenderDependencies} captured in that closure can go stale. Overlay dashboard-owned
 * fields from {@link DashboardOpLayoutModuleDependenciesContext} so module renderers stay fresh.
 */
function OpLayoutEngineDashboardModuleDependencies({
  dependencies,
  children,
}: {
  dependencies: ModuleRenderDependencies
  children: (dependencies: ModuleRenderDependencies) => React.ReactElement
}) {
  const dashboardOpLayoutModuleDependencies = useContext(DashboardOpLayoutModuleDependenciesContext)
  const mergedModuleRenderDependencies = useMemo(
    () => mergeModuleRenderDependenciesWithDashboardOpLayout(dependencies, dashboardOpLayoutModuleDependencies),
    [dependencies, dashboardOpLayoutModuleDependencies],
  )
  return children(mergedModuleRenderDependencies)
}

const MODULE_RENDERERS: Record<ModuleType, ModuleRenderer> = {
  [MODULE_TYPES.ASSET_VIEW]: (dependencies, moduleId) => {
    const isTransientDrilldown = dependencies.config?.transient === true
      && dependencies.config?.drilldownSeed != null

    if (isTransientDrilldown) {
      // Lazy-required so module-renderers stays free of a static hooks/redux import
      // (it is imported by non-React call sites such as use-dashboard-module-actions).
      const TransientDrilldownAssetViewPanel = require("./transient-drilldown-panel").default
      return (
        <div className="dv-panel-asset-view dv-panel-asset-view--drilldown">
          <TransientDrilldownAssetViewPanel moduleId={moduleId} />
        </div>
      )
    }

    const AssetViewWrapper = require("connected-components/asset-view").default
    return (
      <div className="dv-panel-asset-view">
        <OpLayoutEngineDashboardModuleDependencies dependencies={dependencies}>
          {(d) => (
            <AssetViewWrapper
              instrumentsWithMetadata={d.instrumentsWithMetadata}
              hasMultipleInstruments={d.hasMultipleInstruments}
              contentSets={d.contentSets}
              initialView={d.config?.initialView}
              duplicateFromPanelId={d.config?.duplicateFromPanelId}
              shouldUseLegacyPreferences={d.shouldUseLegacyAssetViewPreferences === true}
              syncDashboardModuleConfig={d.syncDashboardModuleConfig}
              drilldownSeed={d.config?.drilldownSeed}
            />
          )}
        </OpLayoutEngineDashboardModuleDependencies>
      </div>
    )
  },

  [MODULE_TYPES.KPI]: (dependencies) => {
    const KPIWrapper = require("../../kpi").default
    return (
      <div className="dv-panel-kpi">
        <OpLayoutEngineDashboardModuleDependencies dependencies={dependencies}>
          {(d) => (
            <KPIWrapper
              instrumentsWithMetadata={d.instrumentsWithMetadata}
              contentSets={d.contentSets}
              initialTab={d.config?.initialTab}
              initialDateOverride={d.config?.dateOverride}
            />
          )}
        </OpLayoutEngineDashboardModuleDependencies>
      </div>
    )
  },

  [MODULE_TYPES.GRAPH]: (dependencies) => {
    const GraphWrapper = require("../../graph").default
    return (
      <div className="dv-panel-graph">
        <OpLayoutEngineDashboardModuleDependencies dependencies={dependencies}>
          {(d) => (
            <GraphWrapper
              instrumentsWithMetadata={d.instrumentsWithMetadata}
              modelMetadata={d.modelMetadata}
              initialSelectedTemplate={d.config?.selectedTemplate}
              initialDateOverride={d.config?.dateOverride}
            />
          )}
        </OpLayoutEngineDashboardModuleDependencies>
      </div>
    )
  },

  [MODULE_TYPES.FACTORS]: (dependencies) => {
    const FactorsViewWrapper = require("connected-components/factors-view").default
    return (
      <div className="dv-panel-factors">
        <OpLayoutEngineDashboardModuleDependencies dependencies={dependencies}>
          {(d) => (
            <FactorsViewWrapper
              instrumentsWithMetadata={d.instrumentsWithMetadata}
              contentSets={d.contentSets}
              moduleConfig={d.config}
            />
          )}
        </OpLayoutEngineDashboardModuleDependencies>
      </div>
    )
  },

  [MODULE_TYPES.AI_ARTIFACT]: (dependencies, moduleId) => {
    const AiArtifactModule = require("../modules/ai-artifact").default
    const ArtifactRenderer = require("renderers/ai-artifact").default
    return (
      <div className="dv-panel-ai-artifact">
        <ErrorBoundary
          fallback={
            <ArtifactRenderer
              status={ERROR}
              data={null}
              title={dependencies.config?.title}
            />
          }
        >
          <AiArtifactModule moduleId={moduleId} />
        </ErrorBoundary>
      </div>
    )
  },
}

function createModuleComponent(
  module: DashboardModule,
  dependencies: CreateComponentDependencies,
  allModules: DashboardModule[],
): OPLayoutComponent | null {
  const { type, config } = module

  if (!isModuleTypeRegistered(type)) {
    warn(`Module type "${type}" is not registered, skipping render`)
    return null
  }

  const renderer = MODULE_RENDERERS[type]
  if (!renderer) {
    warn(`No renderer found for module type: ${type}`)
    return null
  }

  const definition = getModuleDefinition(type)

  const shouldUseLegacyAssetViewPreferences = getShouldUseLegacyAssetViewPreferences(module, allModules)

  const renderDependencies: ModuleRenderDependencies = {
    ...dependencies,
    config,
    ...(type === MODULE_TYPES.ASSET_VIEW && {
      shouldUseLegacyAssetViewPreferences,
      ...(dependencies.updateModule && shouldUseLegacyAssetViewPreferences && {
        syncDashboardModuleConfig: createAssetViewSyncDashboardModuleConfig(
          dependencies.updateModule,
          module.id,
        ),
      }),
    }),
  }

  const title = config.title || definition.title
  const removable = isModuleRemovable(module, definition)

  return {
    component: getModuleComponentName(type, module.id),
    title,
    ...(config.floating === true && {
      floating: true,
      ...(config.floatingClickPosition && { floatingClickPosition: config.floatingClickPosition }),
    }),
    ...(config.transient === true && { transient: true }),
    icon: definition.icon,
    renderer: (props) => (
      <PanelIdProvider value={props.api.id}>
        <PanelTitleProvider value={title}>
          {renderer(renderDependencies, module.id)}
        </PanelTitleProvider>
      </PanelIdProvider>
    ),
    removable,
    removeRequiresConfirmation: definition.removeRequiresConfirmation === true,
    removePanel: removable && dependencies.onRemoveModule
      ? (panelId, requiresConfirmation) => {
        dependencies.onRemoveModule?.(module.id, requiresConfirmation)
      }
      : undefined,
    editable: definition.editable,
    onUpdateTitle: definition.editable && dependencies.onUpdateModuleTitle
      ? (panelId: string, newTitle: string) => {
        dependencies.onUpdateModuleTitle?.(module.id, newTitle)
        downloadRegistry.updateSheetNamesForPanel(panelId, newTitle)
      }
      : undefined,
    duplicatePanel: dependencies.onDuplicateModule
      ? () => dependencies.onDuplicateModule?.(module.id)
      : undefined,
  }
}

export function createModuleComponents(
  modules: DashboardModule[],
  dependencies: CreateComponentDependencies,
): OPLayoutComponent[] {
  return modules
    .map((module) => createModuleComponent(module, dependencies, modules))
    .filter((component): component is OPLayoutComponent => component !== null)
}
